import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type BioExamObservation from "./loinc";

//---- Automatically generated, don't update this part ----

type FrequencyStringEnum =
  | "1-day"
  | "2-day"
  | "3-day"
  | "1-week"
  | "2-week"
  | "1-month"
  | "2-month"
  | "3-month"
  | "4-month"
  | "5-month"
  | "6-month"
  | "1-year"
  | "never";

type SourceStringEnum = "profiler" | "wise";

/**
 * Represents the bio exams part of the PPS, the aim of the JE is to track the bio exams
 * frequency changes.
 * An instance of this JE corresponds to a bio exam check-up, which includes one or
 * several bio exam observations and a timing (the frequency at which the bio exams have
 * to be done)
 */
export default class BioExamFrequencyChangedJournalEvent extends JournalEvent {
  static type = "bio-exam-frequency-changed-journal-event";

  /**
   * The number of missing declaration before alerting the medic, 0 is for never
   */
  @attr("number") declare forgettingNumber: number;

  /**
   * A patient can have several bio exam check-ups which will be displayed with a
   * specific order, this attribute aim to keep track of the priority order
   */
  @attr("number", {
    defaultValue() {
      return 0;
    }
  })
  declare index: number;

  /**
   * The frequency at which the bio exams of the check-up have to be done
   */
  @attr("string") declare frequency: FrequencyStringEnum;

  /**
   * The list of LOINC of the bio exams of the check-up
   */
  @attr() declare loincs: string[];

  @attr("string") declare source: SourceStringEnum;

  static validations(intl: IntlService) {
    return {
      loincs: validatePresence({
        presence: true,
        description: intl.t(
          "validations.bio-exam-frequency-changed-journal-event.loincs"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.bio-exam-frequency-changed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      forgettingNumber: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-frequency-changed-journal-event.forgetting-number"
        )
      }),
      index: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-frequency-changed-journal-event.index"
        )
      }),
      frequency: validateInclusion({
        list: [
          "1-day",
          "2-day",
          "3-day",
          "1-week",
          "2-week",
          "1-month",
          "2-month",
          "3-month",
          "4-month",
          "5-month",
          "6-month",
          "1-year",
          "never"
        ],
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-frequency-changed-journal-event.frequency"
        )
      }),
      source: validateInclusion({
        list: ["profiler", "wise"],
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-frequency-changed-journal-event.source"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "bioExamFrequencyChangedJournalEvents"
  })
  declare patient: Patient;

  /**
   * Sort two FrequencyStringEnum
   * @param a the first FrequencyStringEnum
   * @param b the second FrequencyStringEnum
   * @returns 0 if equal, negatif if inferior or positif if superior
   */
  static sortByFrequency(a: FrequencyStringEnum, b: FrequencyStringEnum) {
    const frequencies = [
      "1-day",
      "3-day",
      "1-week",
      "2-week",
      "1-month",
      "2-month",
      "3-month",
      "4-month",
      "5-month",
      "6-month"
    ];
    return frequencies.indexOf(a) - frequencies.indexOf(b);
  }

  get bioExamObservations(): BioExamObservation[] {
    return this.loincs.map(obsLoinc => {
      return this.store.peekRecord("loinc", obsLoinc) as BioExamObservation;
    });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "bio-exam-frequency-changed-journal-event": BioExamFrequencyChangedJournalEvent;
  }
}
